import * as THREE from 'three';
import { fract, pulse } from './critter-math.js';
import { OVERLAY_ORDER, bakeCritter, parts, setDrawLayer } from './critter-parts.js';
import type { BirdSpecies } from './bird-species.js';

/** What the bird is doing, described by amounts so every state blends into the next. */
export interface BirdAction {
  /** 0–1 how much the bird is striding, and where in the stride it is (whole cycles; the two feet are half a cycle apart). */
  walk: number; stride: number;
  /** 0–1 airborne: wings out, feet tucked. `flap` is the wingbeat phase in radians, `effort` its strength (0 glides). */
  air: number; flap: number; effort: number;
  /** 0–1 coiled to spring off the ground, and 0–1 braking to land with cupped wings and feet forward. */
  crouch: number; flare: number;
  /** Roll in radians (positive banks right) and nose-up pitch in radians, for flight. */
  bank: number; pitch: number;
  /** Seconds on the bird's own idle clock: where in its looking, pecking and preening it is. */
  idle: number;
}
export const restingAction = (): BirdAction => ({ walk: 0, stride: 0, air: 0, flap: 0, effort: 0, crouch: 0, flare: 0, bank: 0, pitch: 0, idle: 0 });

/** Body height with the feet planted, in the model's own metres (a crow is about 0.45 m long; every species shares one skeleton and stands this tall). */
export const BIRD_STAND_HEIGHT = .148;
/** How far the shoulder swings a folded wing back along the body, in radians. */
const FOLD = 1.5, ROLL = -.8, STRIDE_SWING = .62;

/**
 * A low-poly bird facing +Z, dressed as the given species. Wings have a shoulder, a wrist and three fingered primaries; legs
 * have a hip and an ankle so feet stay level through a stride; the neck swings the head for looks, pecks and the walking
 * head-bob. Species differ in plumage, wing and beak proportions, and in whether they stretch their wings while standing.
 */
export class BirdModel {
  readonly root = new THREE.Group();
  private tilt = new THREE.Group();
  private neck = new THREE.Group();
  private head = new THREE.Group();
  private tail = new THREE.Group();
  private shoulders: THREE.Group[] = []; private wings: THREE.Group[] = []; private elbows: THREE.Group[] = []; private fingers: THREE.Mesh[][] = [];
  private hips: THREE.Group[] = []; private ankles: THREE.Group[] = [];
  private overlay: boolean | null = null;

  /** `seed` offsets this bird's idle clock so a flock never pecks or preens in step. */
  constructor(readonly species: BirdSpecies, private seed = 0) {
    const { root, tilt, neck, head, tail } = this, o = true, plumage = species.plumage, { wing: W, chord: C, beak: B } = species.build;
    root.add(tilt);
    // Body: a long dark ellipsoid, a broad back with a blue-violet sheen, and a deep chest.
    parts.blob(tilt, plumage.body, [.075, .07, .165], [0, 0, 0], o);
    parts.blob(tilt, plumage.back, [.078, .045, .15], [0, .035, -.02], o);
    parts.blob(tilt, plumage.sheen, [.05, .02, .09], [0, .066, -.03], o);
    parts.blob(tilt, plumage.body, [.065, .06, .07], [0, .01, .1], o);
    neck.position.set(0, .045, .13); tilt.add(neck);
    parts.blob(neck, plumage.body, [.045, .05, .06], [0, .02, .025], o);
    head.position.set(0, .06, .06); neck.add(head);
    parts.blob(head, plumage.body, [.05, .05, .062], [0, 0, 0], o);
    const beak = parts.cone(head, plumage.beak, .026 / Math.sqrt(B), .105 * B, o); beak.rotation.x = Math.PI / 2; beak.position.set(0, -.008, .09 + (B - 1) * .05);
    parts.blob(head, plumage.beak, [.034, .014, .05], [0, .008, .065], o).rotation.x = -.1;
    // A gull's bill carries a red spot near the tip of the lower mandible.
    if (plumage.spot !== undefined) parts.blob(head, plumage.spot, [.009, .007, .012], [0, -.022, .15 + (B - 1) * .1], o);
    for (const side of [-1, 1]) parts.blob(head, plumage.eye, [.009, .009, .009], [side * .037, .014, .028], o);
    tail.position.set(0, 0, -.15); tilt.add(tail);
    const fan = parts.feathers(tail, plumage.back, .2, .08, .16, -.015, o); fan.rotation.y = Math.PI / 2; fan.position.set(.0, 0, 0);
    // Wings. Each shoulder is mirrored by scale, so a shoulder's rotation has the opposite sign on the two sides. The wing
    // shortens as it folds, the way layered feathers do, so a folded wing lies along the flank instead of trailing past the tail.
    for (const side of [-1, 1]) {
      const shoulder = new THREE.Group(); shoulder.position.set(side * .06, .045, .05); shoulder.scale.x = side; tilt.add(shoulder);
      const wing = new THREE.Group(); shoulder.add(wing);
      parts.feathers(wing, plumage.wing, .27 * W, .21 * C, .18 * C, .02, o);
      const elbow = new THREE.Group(); elbow.position.set(.27 * W, 0, -.025); wing.add(elbow);
      parts.feathers(elbow, plumage.wing, .2 * W, .18 * C, .11 * C, .09, o);
      const fingers = [0, 1, 2].map(i => {
        const finger = parts.feathers(elbow, plumage.tip, (.17 - i * .014) * W, .07 * C, .03, .03, o);
        finger.position.set(.18 * W, .001 + i * .0006, -.075 * C - i * .02); return finger;
      });
      this.shoulders.push(shoulder); this.wings.push(wing); this.elbows.push(elbow); this.fingers.push(fingers);
    }
    // Legs: slender dark shins with an ankle joint and a three-toed foot with a hind spur.
    for (const side of [-1, 1]) {
      const hip = new THREE.Group(); hip.position.set(side * .032, -.05, 0); tilt.add(hip);
      parts.stick(hip, plumage.legs, .008, .09, o).position.y = -.045;
      const ankle = new THREE.Group(); ankle.position.set(0, -.09, 0); hip.add(ankle);
      for (const toe of [-.35, 0, .35]) {
        const foot = parts.stick(ankle, plumage.legs, .005, .05, o); foot.rotation.set(Math.PI / 2, 0, toe); foot.position.set(-Math.sin(toe) * .02, -.003, .022);
      }
      const spur = parts.stick(ankle, plumage.legs, .005, .03, o); spur.rotation.x = Math.PI / 2; spur.position.set(0, -.003, -.014);
      this.hips.push(hip); this.ankles.push(ankle);
    }
    bakeCritter(root); this.setOverlay(false);
  }

  /** Draw after the fog veil (high flight) or with the world (on the ground, where the veil should cover it). */
  setOverlay(on: boolean) {
    if (on === this.overlay) return;
    this.overlay = on; setDrawLayer(this.root, on ? OVERLAY_ORDER : 0);
  }

  apply(a: BirdAction) {
    const t = a.idle + this.seed, ground = 1 - a.air, still = ground * (1 - a.walk) * (1 - a.crouch);
    // Idle behaviours repeat on their own slow cycles, so no two birds' pecks or preens line up.
    const peckCycle = fract(t / 5.7), peck = (pulse(peckCycle, .5, .08) + pulse(peckCycle, .62, .08) * .8) * still;
    const preenCycle = fract(t / 13.3 + .3), preen = pulse(preenCycle, .55, .22) * still;
    const shake = pulse(fract(t / 17.1), .8, .05) * still;
    // Species that stretch rise slowly on the spot, spread a wing out and shiver it, then settle.
    const stretch = this.species.stretches ? pulse(fract(t / 23.3 + .4), .5, .09) * still : 0;
    const look = (Math.sin(t * .83) * .5 + Math.sin(t * 1.9 + 1.3) * .22) * ground * (1 - a.walk * .7);
    const breath = Math.sin(t * 2.6) * .004 * still;

    // Attitude: roll and pitch in flight, nose-down pecks, a lean into each stride and a rustle when shaking out.
    this.tilt.rotation.set(-a.pitch + peck * .5 + a.walk * .1 + a.crouch * .35 - a.flare * .2, 0, -a.bank + Math.sin(t * 40) * shake * .05, 'YXZ');
    // With legs splayed mid-stride the body sinks to where its feet reach the ground; with legs upright it stands tallest.
    const splay = .09 * (1 - Math.cos(STRIDE_SWING * Math.abs(Math.sin(a.stride * Math.PI * 2)))) * a.walk;
    this.tilt.position.set(0, BIRD_STAND_HEIGHT - a.crouch * .045 - peck * .025 + breath - splay - Math.sin(a.flap) * .012 * a.effort * a.air, 0);

    // Neck and head: the walk's head-bob, looks, pecks, preening, and a straight-out neck in flight.
    const bob = Math.sin(a.stride * Math.PI * 2 - .6) * a.walk;
    this.neck.position.set(0, .045, .13 + bob * .014 + a.air * .01);
    this.neck.rotation.set(-a.walk * .1 + a.air * .1 + a.flare * .25, look * .7 + preen * .9 + Math.sin(t * 40) * shake * .3, preen * .25);
    this.head.rotation.set(peck * .75 - a.walk * .05 + preen * .35 - a.air * .1 + a.flare * .1, look * .6 - preen * .4, 0);
    this.tail.rotation.set(-.12 - a.flare * .55 + Math.sin(t * 1.3) * .03 + a.walk * .08 + a.air * .06, Math.sin(t * 40) * shake * .25, 0);
    this.tail.scale.set(1, 1, 1 + a.flare * .45);

    // Wings: folded on the ground, opened a little to coil or shuffle, fully spread and beating in the air.
    const open = Math.max(a.air, a.crouch * .5, shake * .3, preen * .12, stretch * .85);
    const stroke = a.air * (.6 * a.effort + a.flare * .35);
    const wrist = a.air * (Math.sin(a.flap - .9) * (.5 * a.effort + a.flare * .25) - .08 - a.flare * .35);
    this.shoulders.forEach((shoulder, index) => {
      const side = index ? 1 : -1;
      shoulder.rotation.set(0, side * (1 - open) * FOLD, side * (.1 * open + Math.sin(a.flap) * stroke + Math.sin(t * 9) * .12 * stretch));
      this.wings[index].scale.x = .5 + .5 * open;
      this.wings[index].rotation.x = (1 - open) * ROLL;
    });
    this.elbows.forEach(elbow => elbow.rotation.set(0, (1 - open) * .3, wrist));
    this.fingers.forEach(group => group.forEach((finger, i) => { finger.rotation.y = (i - 1) * .15 * open * (1 + a.flare * .6); }));

    // Legs: alternating swing and lift when walking, tucked back in the air, forward to land.
    const tucked = a.air * (1 - a.flare), forward = a.flare * a.air;
    this.hips.forEach((hip, index) => {
      const cycle = a.stride + index * .5, swing = Math.sin(cycle * Math.PI * 2) * STRIDE_SWING * a.walk, lift = Math.max(0, Math.cos(cycle * Math.PI * 2)) * .75 * a.walk;
      hip.rotation.set(-swing * ground + tucked * 1.15 - forward * .9 + a.crouch * .4, 0, 0);
      // The swinging leg draws up (shortening it) and its toes curl up out of the way; the planted foot stays flat.
      hip.scale.y = 1 - lift * .22;
      this.ankles[index].rotation.set(swing * ground - lift * .5 - tucked * .9 + forward * .5 - a.crouch * .5, 0, 0);
    });
  }
}
